import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useContext,
} from "react";
import {
  Row,
  Badge,
  Button,
  useAPI,
  Grid,
  ClassHelper,
  ViewContext,
  useLocation,
  Loader,
} from "components/lib";
import { Form, Message } from "components/lib";
import Axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaste } from "@fortawesome/free-solid-svg-icons";
import Style from "../../components/form/input/input.tailwind.js";
import MaterialReactTable from "material-react-table";

const {
  getXpmRecordType,
  getXpmRecordTypeLabel,
} = require("./xpm-record-type");

// Wire values are decoupled from the labels: Radio reads `option.value` and
// Fieldset preselects via `options.indexOf(props.default)`, which only matches on
// the same object reference — so `default` below is looked up out of this array
// rather than rebuilt from the chosen value.
const VERIFICATION_TYPES = [
  { label: "Full AML check", value: "tranche2" },
  { label: "Biometric only", value: "biometric_only" },
];

export function DirectVerification(props) {
  const [directVerificationResponse, setDirectVerificationResponse] = useState(
    {}
  );
  // One object rather than five fields: every Fill replaces it wholesale, which
  // is what guarantees the form re-seeds even when the picked row carries the
  // same values as the last one.
  const [prefill, setPrefill] = useState({
    firstName: "",
    lastName: "",
    email: "",
    triggeredBy: "direct",
    contactId: "",
  });
  const [refreshForm, setRefreshForm] = useState(false);
  const [searchTerm, setSeachTerm] = useState("");
  const xeroConnected = useAPI("/api/integration/xero/connected");
  const pipeDriveConnected = useAPI("/api/integration/pipedrive/connected");
  // Not useAPI: it never clears its loading state on error, so a failed /me would
  // leave this permanently unresolved. Settling in both directions lets the form
  // render exactly once, with the field list already correct — no remount, so
  // nothing the operator has typed is discarded.
  const [me, setMe] = useState(null);
  const tranche2Enabled = me?.tranche2_enabled === true;
  const [pipedrivePersons, setPipedrivePersons] = useState([]);
  const [pipedriveSearchTerm, setPipedriveSearchTerm] = useState("");
  const [pipedriveLoading, setPipedriveLoading] = useState(false);
  // const xeroConnections = useAPI("/api/integration/xero/connections");
  const [xeroConnections, setXeroConnections] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [xeroContacts, setXeroContacts] = useState([]);
  const [selectedRow, setSelectedRow] = useState({});

  // The operator's own radio choices. Refs, not state: they only need to be
  // read when the form data is rebuilt for a prefill, and holding them as
  // state would rebuild it on every radio click — re-seeding the text inputs
  // from the prefill values and discarding anything typed since.
  const emailUserChoice = useRef(undefined);
  const verificationTypeChoice = useRef(VERIFICATION_TYPES[0].value);

  const copy = <FontAwesomeIcon icon={faPaste} />;
  const viewContext = useContext(ViewContext);
  const [refreshCount, setRefreshCount] = useState(0);
  const location = useLocation();

  const textStyle = ClassHelper(Style, {
    textbox: true,
    className: props.className,
    error: props.valid === false,
    success: props.valid === true,
    warning: props.warning,
  });

  const columns = useMemo(
    () => [
      {
        accessorKey: "Name", //access nested data with dot notation
        header: "Name",
      },
      {
        accessorKey: "EmailAddress",
        header: "Email",
      },
      // XPM interleaves each practice client with its own contacts, and the two
      // routinely share a name and an email address — so before hitting Fill an
      // operator has nothing to tell them apart. The distinction is not
      // cosmetic: it decides whether the AML status writeback targets
      // client.api/update or client.api/contact. Keep this column
      // unconditional; MRT freezes column order at mount, so one added later
      // would render past the Fill button.
      {
        id: "recordType",
        header: "Contact Type",
        // Deliberately not a select filter: a plain Xero organisation tenant
        // produces no record types at all, so a fixed Client/Contact option
        // list would offer two choices that match nothing and read as broken.
        // Always a string: MRT calls .toString() on cell values while filtering
        // and faceting. Plain Xero organisation contacts carry no triggered_by
        // and resolve to "" — they are left unlabelled rather than guessed at.
        accessorFn: (row) => getXpmRecordTypeLabel(row.triggered_by),
        Cell: ({ row }) => {
          const { label, color } = getXpmRecordType(row.original.triggered_by);
          return label ? <Badge text={label} color={color} /> : null;
        },
      },
      {
        accessorKey: "ContactId",
        header: "ContactId",
        hidden: true,
      },
    ],
    []
  );

  const pipedriveColumns = useMemo(
    () => [
      {
        accessorKey: "item.name", //access nested data with dot notation
        header: "Name",
      },
      {
        accessorKey: "item.primary_email",
        header: "Email",
      },
      {
        accessorKey: "item.id",
        header: "Contact Id",
        hidden: true,
      },
      {
        accessorKey: "triggered_by",
        header: "triggered_by",
        hidden: true,
        default: "pipedrive",
      },
    ],
    []
  );

  const searchView = async () => {
    console.log("in here");
    // add text to firstName element
    // document.getElementsByName("firstName").value = "John";
    const type =
      xeroConnections.filter((x) => x.tenantId === selectedAccount)[0]
        .tenantType || "";
    const xeroContactsResponse = await Axios.get(
      "/api/integration/xero/contacts?searchTerm=" +
        searchTerm +
        "&tenantId=" +
        selectedAccount +
        "&tenantType=" +
        type
    );
    console.log("xeroContactsResponse: ", xeroContactsResponse);
    setXeroContacts(xeroContactsResponse?.data?.data?.Contacts);

    // setRefreshForm(true);
  };

  const updateForm = () => {
    // Guarded: an XPM row can arrive with no Name, and an unguarded split here
    // throws inside the prefill effect, which blanks the whole page.
    const name = selectedRow?.Name || "";

    // Replaced wholesale rather than field by field, so picking the same row
    // twice still produces a new object and re-seeds the form. Re-picking a row
    // the operator has hand-edited has to discard the edit.
    setPrefill({
      firstName: selectedRow?.FirstName || name.split(" ")[0] || "",
      lastName:
        selectedRow?.LastName || name.split(" ").slice(1).join(" ") || "",
      email: selectedRow?.EmailAddress || "",
      contactId: selectedRow?.ContactId || "",
      triggeredBy: selectedRow?.triggered_by || "direct",
    });
  };

  const updatePipedriveForm = () => {
    const name = selectedRow?.item?.name || "";

    setPrefill({
      firstName: name.split(" ")[0] || "",
      lastName: name.split(" ").slice(1).join(" ") || "",
      email: selectedRow?.item?.primary_email || "",
      contactId: selectedRow?.item?.id || "",
      triggeredBy: selectedRow?.triggered_by || "pipedrive",
    });
  };

  // useEffect(() => {
  //   if (xeroConnections?.data?.length > 0) {
  //     setSelectedAccount(xeroConnections?.[0]?.tenantId);
  //   }
  // }, [xeroConnections]);

  useEffect(() => {
    // console.log("refreshForm: ", refreshForm);
    if (refreshForm) {
      if (selectedRow.item) {
        updatePipedriveForm();
      } else {
        updateForm();
      }
      setRefreshForm(false);
    }
  }, [refreshForm]);

  useEffect(() => {
    let live = true;
    const settle = (data) => live && setMe(data || {});
    Axios.get("/api/direct-verification/me")
      .then((res) => settle(res?.data?.data))
      // Settle to {} on failure so the form still renders. The account then looks
      // Tranche 2-disabled and the choice is hidden, but the server re-derives the
      // flag from its own settings, so the journey itself is unaffected.
      .catch(() => settle({}));
    return () => {
      live = false;
    };
  }, []);

  useEffect(async () => {
    if (!xeroConnected?.data) {
      return;
    }
    const xeroConnectionsResponse = await Axios.get(
      "/api/integration/xero/connections"
    );
    console.log("xeroConnectionsResponse: ", xeroConnectionsResponse);
    setXeroConnections(xeroConnectionsResponse?.data?.data);
    setSelectedAccount(xeroConnectionsResponse?.data?.data?.[0]?.tenantId);
  }, [xeroConnected]);

  useEffect(() => {
    // was an error message passed from the server router?
    const qs = location.search;

    if (qs.includes("error") && refreshCount < 1) {
      setRefreshCount(refreshCount + 1);
      const msg = decodeURIComponent(qs.substring(qs.indexOf("?error=") + 7));
      viewContext.notification.show(msg, "error");
    }
  }, [location.search, viewContext.notification, xeroConnected]);

  // Memoised so its identity only changes when a prefill lands. `Form` re-seeds
  // itself from this object on every render under `updateOnChange`, so a fresh
  // object each render would wipe whatever the operator had typed — including
  // on unrelated re-renders such as typing in the search box above. A stable
  // identity also lets Form's in-place value updates survive, which is how the
  // signup form works.
  const formData = useMemo(
    () => ({
      firstName: {
        label: "First name",
        type: "text",
        required: true,
        errorMessage: "Please enter first name.",
        value: prefill.firstName,
      },
      lastName: {
        label: "Last name",
        type: "text",
        required: true,
        errorMessage: "Please enter last name.",
        value: prefill.lastName,
      },
      email: {
        label: "Email",
        type: "email",
        required: true,
        errorMessage: "Please enter email address.",
        value: prefill.email,
      },
      emailUser: {
        label: "Email User",
        type: "radio",
        options: ["Yes", "No"],
        required: true,
        errorMessage: "Please choose to email user or not.",
        // Seeded from the operator's last choice so a prefill rebuild does not
        // silently blank it.
        default: emailUserChoice.current,
        value: emailUserChoice.current,
      },
      // Only offered when the account's Tranche 2 module is on. `value` seeds
      // the payload so an untouched radio still submits the full check;
      // `default` drives the visual preselect and must be the same object
      // reference Fieldset compares against.
      ...(tranche2Enabled && {
        verificationType: {
          label: "Verification type",
          type: "radio",
          options: VERIFICATION_TYPES,
          default: VERIFICATION_TYPES.find(
            (type) => type.value === verificationTypeChoice.current
          ),
          value: verificationTypeChoice.current,
          required: true,
          errorMessage: "Please choose a verification type.",
        },
      }),
      type: {
        type: "hidden",
        value: "kyc",
      },
      triggeredBy: {
        type: "hidden",
        value: prefill.triggeredBy,
      },
      active: {
        type: "hidden",
        value: true,
      },
      contactId: {
        type: "hidden",
        value: prefill.contactId,
      },
    }),
    // The radio choices are deliberately absent: they live in refs precisely so
    // that changing one does not rebuild the form and reset the text inputs.
    [prefill, tranche2Enabled]
  );

  return (
    <div>
      {xeroConnections?.length > 0 && (
        <Grid>
          <Row>
            <h3>Find Xero Contacts</h3>
            {xeroConnections?.length > 0 && (
              <>
                {console.log("xeroConnections: ", xeroConnections)}
                <Form
                  data={{
                    account: {
                      label: "Account",
                      type: "select",
                      options: xeroConnections?.map((connection) => {
                        return {
                          value: connection.tenantId,
                          label: connection.tenantName,
                        };
                      }),
                      required: true,
                      default: xeroConnections?.[0]?.tenantId,
                      errorMessage: "Please select an account.",
                    },
                  }}
                  updateOnChange
                  onChange={(data) => {
                    console.log("data: ", data);
                    setSelectedAccount(data.value);
                  }}
                />
              </>
            )}
            <br />
            <span>
              <input
                type="text"
                className={textStyle}
                placeholder="Enter your xero contact's name or email or just search"
                onChange={(e) => {
                  setSeachTerm(e.target.value);
                }}
              />
              <br />
              <Button text="Search" action={searchView} small />
            </span>

            <br />
            <br />

            {xeroContacts?.length > 0 && (
              <MaterialReactTable
                columns={columns}
                data={xeroContacts}
                enablePagination={true}
                enableRowActions
                positionActionsColumn="last"
                initialState={{
                  columnVisibility: { ContactId: false },
                }}
                renderRowActions={(row, index) => [
                  <Button
                    action={(row) => {
                      console.info("Fill", row);
                      // navigate("/verification/" + row.row.original.id);
                      setSelectedRow(row.row.original);
                      setRefreshForm(true);
                    }}
                    params={row}
                    text="Fill"
                    outline
                    small
                  />,
                ]}
              />
            )}
          </Row>
        </Grid>
      )}
      {pipeDriveConnected?.data && (
        <Grid>
          <Row>
            <h3>Find Pipedrive Contacts</h3>
            <div>
              <input
                type="text"
                label="Search Term"
                className={textStyle}
                placeholder="Enter your Pipedrive contact person's name or email or other properties. Minimum 2 characters."
                required
                minLength={2}
                onChange={(e) => setPipedriveSearchTerm(e.target.value)}
              />
              <br />
              <Button
                text="Search"
                action={async () => {
                  setPipedriveLoading(true);
                  const response = await Axios.get(
                    "/api/integration/pipedrive/search?q=" + pipedriveSearchTerm
                  );
                  console.log("response: ", response);
                  setPipedrivePersons(response?.data?.data?.items || []);
                  setPipedriveLoading(false);
                }}
              />

              <br />
              <br />

              {pipedriveLoading && <Loader />}

              {pipedrivePersons?.length > 0 && (
                <MaterialReactTable
                  columns={pipedriveColumns}
                  data={pipedrivePersons}
                  enablePagination={true}
                  enableRowActions
                  positionActionsColumn="last"
                  initialState={{
                    columnVisibility: { "item.id": false, triggered_by: false },
                  }}
                  renderRowActions={(row, index) => [
                    <Button
                      action={(row) => {
                        console.info("Fill", row);
                        setSelectedRow(row.row.original);
                        setRefreshForm(true);
                      }}
                      params={row}
                      text="Fill"
                      outline
                      small
                    />,
                  ]}
                />
              )}
            </div>
          </Row>
        </Grid>
      )}
      <Row>
        {!me && <Loader />}
        {me && (
          <Form
            data={formData}
            // Lets Form pick up a prefill from `formData` without being
            // unmounted to do it. The unmount used to reset the Verification
            // type radio back to "Full AML check" and blank Email User on
            // every Fill, silently discarding the operator's choice.
            updateOnChange
            onChange={({ input, value }) => {
              if (input === "emailUser") emailUserChoice.current = value;
              if (input === "verificationType")
                verificationTypeChoice.current = value;
            }}
            method="POST"
            url="/api/direct-verification"
            callback={async (res) => {
              console.log("response:::: ", res.data);
              setDirectVerificationResponse(res.data);
              if (res.data.message == "verification created") {
                window.location.href = `/verification/${res.data.data.verificationData.id}`;
              }
            }}
            buttonText="Verify"
          />
        )}

        {directVerificationResponse.data !== null &&
          directVerificationResponse.data !== undefined && (
            <div>
              <br />
              <br />
              <p>
                {directVerificationResponse.data.idUrl} {"      "}
                <span
                  className="sg-icon-copy"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      directVerificationResponse.data.idUrl
                    );
                  }}
                >
                  {copy}
                </span>
              </p>
            </div>
          )}
      </Row>
    </div>
  );
}
